import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, ShoppingBag, Compass, BookOpen, Music, Bell, CheckCircle2, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ShoppingComingSoonPage() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Please share your first name and email so we know who to write to.");
      return;
    }
    setSubscribing(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        name: name.trim(),
        email: email.trim(),
        source: "shopping",
      });
      setSubscribed(true);
      toast.success("You're on the list!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong — please try again.");
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-40 backdrop-blur-lg bg-opacity-95">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              data-testid="shopping-back-btn"
              onClick={() => navigate("/")}
              aria-label="Back to home"
              className="text-text-secondary hover:text-primary transition-colors p-1"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              data-testid="shopping-brand-home"
              onClick={() => navigate("/get-started")}
              className="flex items-center"
              aria-label="Explore care types"
            >
              <img src="/tyvena-lockup-horizontal.png" alt="Tyvena" className="h-9 md:h-10 w-auto" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-14 md:py-20">
        {/* Warm coming-soon card */}
        <div
          data-testid="shopping-coming-soon"
          className="bg-white rounded-3xl border-2 border-primary/25 p-10 md:p-14 text-center shadow-sm"
        >
          <div className="inline-flex w-20 h-20 rounded-full bg-primary/10 items-center justify-center mb-6">
            <ShoppingBag className="w-10 h-10 text-primary" strokeWidth={1.5} />
          </div>

          <p className="text-xs md:text-sm font-body font-bold uppercase tracking-[0.2em] text-primary mb-3">
            Coming soon
          </p>
          <h1 className="text-3xl md:text-5xl font-heading font-extrabold text-accent leading-[1.1] mb-5">
            A caregiver&apos;s shop is on the way.
          </h1>
          <p className="font-body text-text-secondary text-lg leading-relaxed max-w-xl mx-auto">
            We&apos;re curating a small, thoughtful set of products caregivers actually reach for — grab bars, shower chairs, comfort items, dementia-friendly clocks. Nothing flashy, nothing sold to you. Just the things that quietly make a hard day easier.
          </p>
          <p className="font-body text-text-primary text-lg mt-5 max-w-xl mx-auto italic">
            Standing at the threshold doesn&apos;t mean you&apos;re lost. It means you&apos;re about to enter something new.
          </p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto">
            <button
              data-testid="cs-cta-books"
              onClick={() => navigate("/books")}
              className="group flex flex-col items-center gap-2 bg-[#EDF7F6] hover:bg-primary/15 border border-primary/20 rounded-2xl p-5 transition-all"
            >
              <BookOpen className="w-6 h-6 text-primary group-hover:scale-110 transition-transform" />
              <span className="font-body font-semibold text-sm text-text-primary">Explore Books</span>
              <span className="font-body text-xs text-text-secondary">Caregiver reading list</span>
            </button>
            <button
              data-testid="cs-cta-music"
              onClick={() => navigate("/music")}
              className="group flex flex-col items-center gap-2 bg-secondary/10 hover:bg-secondary/20 border border-secondary/30 rounded-2xl p-5 transition-all"
            >
              <Music className="w-6 h-6 text-secondary group-hover:scale-110 transition-transform" />
              <span className="font-body font-semibold text-sm text-text-primary">Music &amp; Comfort</span>
              <span className="font-body text-xs text-text-secondary">Calming playlists</span>
            </button>
            <button
              data-testid="cs-cta-asher"
              onClick={() => navigate("/chat")}
              className="group flex flex-col items-center gap-2 bg-accent/5 hover:bg-accent/10 border border-accent/25 rounded-2xl p-5 transition-all"
            >
              <Compass className="w-6 h-6 text-accent group-hover:scale-110 transition-transform" />
              <span className="font-body font-semibold text-sm text-text-primary">Talk to Asher</span>
              <span className="font-body text-xs text-text-secondary">Your care guide</span>
            </button>
          </div>

          <div className="mt-10 pt-6 border-t border-[#E2E8F0]">
            {subscribed ? (
              <div
                data-testid="shopping-subscribed-confirmation"
                className="inline-flex items-center gap-2 text-primary font-body text-base font-semibold"
              >
                <CheckCircle2 className="w-5 h-5" />
                <span>You&apos;re on the list, {name.split(" ")[0]} — we&apos;ll be in touch the day the shop opens.</span>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Bell className="w-4 h-4 text-secondary" />
                  <p className="font-body font-semibold text-text-primary text-sm">
                    Want to know the moment it opens?
                  </p>
                </div>
                <form
                  data-testid="shopping-newsletter-form"
                  onSubmit={handleSubscribe}
                  className="flex flex-col sm:flex-row gap-2 max-w-xl mx-auto"
                >
                  <Input
                    data-testid="shopping-newsletter-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="First name"
                    className="flex-1"
                    aria-label="First name"
                  />
                  <Input
                    data-testid="shopping-newsletter-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="flex-[2]"
                    aria-label="Email"
                  />
                  <Button
                    data-testid="shopping-newsletter-submit"
                    type="submit"
                    disabled={subscribing}
                    className="bg-primary text-white hover:bg-[#238C82] rounded-full font-semibold px-6 whitespace-nowrap"
                  >
                    {subscribing ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Adding you…</>
                    ) : (
                      "Notify Me"
                    )}
                  </Button>
                </form>
                <p className="font-body text-xs text-text-secondary mt-3 italic">
                  We&apos;ll only email you about the shop opening — no other list, no forwarding, unsubscribe anytime.
                </p>
              </>
            )}
          </div>
        </div>

        <p className="text-center mt-8 font-body text-xs text-text-secondary italic">
          Tyvena. Helping you navigate what&apos;s next.
        </p>
      </main>
    </div>
  );
}
